import { FC, useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router";

import { formatDate } from "capitalrx-utilities";
import { useAlert } from "capitalrx-components-2.0/lib/general";
import { Shelf, Stack } from "capitalrx-components-2.0/lib/layout";
import { Heading, PageTitle } from "capitalrx-components-2.0/lib/typography";
import { Spinner } from "capitalrx-components-2.0/lib/general";

import {
  CaseTestClaimForm,
  CaseQuestionnaireForm,
  PACaseCreateForm,
  PAQuestionnaireForm,
  PACreateErrorDisplay,
} from "@/components";
import { createPACaseAPI, getStatePatient } from "@/api";
import { Auth } from "@/api/interfaces";
import { ANALYTIC_EVENTS, logEvent } from "@/service/analyticsService";
import { getThemeURL, stripPhoneFormatting } from "@/utils";

import styles from "./PACreatePage.module.scss";


enum FormStates {
  TestClaim = 1,
  Questionnaire = 2,
}

const ERROR_CODE_TO_EVENT: Record<string, string> = {
  MEMBER_NOT_FOUND: "caseCreateMemberNotFound",
  INVALID_NDC: "caseCreateInvalidNdc",
  INVALID_PROVIDER: "caseCreateInvalidProvider",
  DRUG_NOT_COVERED: "caseCreateDrugNotCovered",
  INVALID_QUESTIONNAIRE: "caseCreateInvalidQuestionnaire",
  DUPLICATE_CASE: "caseCreateDuplicateCase",
  PA_TOOL_NOT_MIGRATED: "caseCreatePaToolNotMigrated",
  EPA_CONFIG_MISSING: "caseCreateEpaConfigMissing",
  INVALID_ICD10: "caseCreateInvalidIcd10",
};


export const PACreatePage: FC = () => {
  const navigate = useNavigate();
  const showAlert = useAlert();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState<boolean>(false);
  const [prefillQuestions, setPrefillQuestions] = useState<Auth.QuestionAnswers>();
  const [createError, setCreateError] = useState<{
    message: string;
    errorCode?: string;
    details?: Record<string, string>;
  } | null>(null);

  const form = useForm<PACaseCreateForm>({
    defaultValues: {
      dispensing_site: "pharmacy",
    },
  });
  const [showPrescriberFaxField, setShowPrescriberFaxField] = useState<boolean>(false);
  const questionnaireForm = useForm<PAQuestionnaireForm>();

  const alternateFax = form.watch("alternate_fax_number");

  const prefillFromState = useCallback(async (oauthState: string) => {
    setLoading(true);
    try {
      // Send claim and navigate to it after
      const statePatient = await getStatePatient({ "oauth_state": oauthState });
      if (statePatient.date_of_birth) {
        form.setValue("member_date_of_birth" as keyof PACaseCreateForm, formatDate(statePatient.date_of_birth, "MM/DD/YYYY"));
      }
      if (statePatient.external_member_id) {
        form.setValue("external_member_id" as keyof PACaseCreateForm, statePatient.external_member_id);
      }
      if (statePatient.first_name) {
        form.setValue("member_first_name" as keyof PACaseCreateForm, statePatient.first_name);
      }
      if (statePatient.last_name) {
        form.setValue("member_last_name" as keyof PACaseCreateForm, statePatient.last_name);
      }
      if (statePatient.provider_npi) {
        form.setValue("npi" as keyof PACaseCreateForm, statePatient.provider_npi);
      }
      if (statePatient.diagnosis_codes) {
        form.setValue("icd_10" as keyof PACaseCreateForm, statePatient.diagnosis_codes);
      }
      if (statePatient.question_answers) {
        setPrefillQuestions(statePatient.question_answers);
      }
    } catch (error) {
      console.error("Failed to get patient state", error);
    }
    setLoading(false);
  }, [form]);

  useEffect(() => {
    const oauthState = searchParams.get("oauth_state");
    if (oauthState) {
      prefillFromState(oauthState);
    }
    setSearchParams({});
  }, [searchParams, prefillFromState]);


  useEffect(() => {
    const hasAlternate = !!alternateFax?.toString().trim();
    setShowPrescriberFaxField(!hasAlternate);
  }, [alternateFax]);

  const getNextStep = () => {
    const formValues = form.getValues();
    if (!formValues?.test_claim || formValues.review_type === "no_review_needed") {
      return FormStates.TestClaim;
    } else {
      return FormStates.Questionnaire;
    }
  };
  const [formProgress, setFormProgress] = useState<FormStates>(getNextStep());

  const goToNextStep = () => {
    setFormProgress(getNextStep());
  };

  const createPriorAuthCase = useCallback(async () => {
    // Clear previous errors
    setCreateError(null);

    // log create submit click
    logEvent(ANALYTIC_EVENTS.PA_CASE_CREATE.create);

    const formVals = form.getValues();
    const sanitizedData = {
      client_id: formVals.test_claim.client_id,
      external_member_id: formVals.test_claim.external_member_id,
      member_first_name: formVals.member_first_name,
      member_last_name: formVals.member_last_name,
      member_date_of_birth: formVals.member_date_of_birth,
      person_code: formVals.test_claim.person_code,
      is_urgent: !!formVals.is_urgent,
      review_type: formVals.review_type,
      drug_ndc: formVals.ndc,
      drug_quantity: formVals.quantity_dispensed,
      drug_days_supply: formVals.days_supply,
      drug_name: formVals.test_claim.drug_name,
      drug_directions_for_use: formVals.directions_for_use,
      icd_10: formVals.icd_10,
      provider_npi: formVals.npi,
      alternate_fax_number: stripPhoneFormatting(formVals.alternate_fax_number),
      dispensing_site: formVals.dispensing_site,
      facility_npi: formVals.facility_npi,
      questionnaire_responses: formVals.questionnaire_responses,
      daw_code: formVals.daw_code,
    };

    try {
      // Send claim and navigate to it after
      const { case_id: caseId } = await createPACaseAPI(sanitizedData);
      goToCase(caseId);

    } catch (error) {
      let errorMessage = "Failed to create prior authorization case";
      let errorCode: string | undefined;
      let details: Record<string, string> | undefined;

      if (axios.isAxiosError(error)) {
        const responseData = error.response?.data as {
          message?: string;
          error_code?: string;
          details?: Record<string, string>;
        };
        const errMsg = (responseData?.message || "").toLowerCase();

        if (errMsg.includes("member not found") || errMsg.includes("invalid member")) {
          errorMessage = "Member not found";
          errorCode = "MEMBER_NOT_FOUND";
        } else if (errMsg.includes("ndc") || errMsg.includes("drug not found")) {
          errorMessage = "Invalid drug NDC";
          errorCode = "INVALID_NDC";
        } else if (errMsg.includes("npi") || errMsg.includes("prescriber") || errMsg.includes("provider")) {
          errorMessage = "Provider NPI is invalid or not found";
          errorCode = "INVALID_PROVIDER";
        } else if (errMsg.includes("not covered") || errMsg.includes("formulary")) {
          errorMessage = "Drug is not covered under member's plan";
          errorCode = "DRUG_NOT_COVERED";
        } else if (errMsg.includes("questionnaire") || errMsg.includes("clinical criteria")) {
          errorMessage = "Required clinical criteria questions are missing or invalid";
          errorCode = "INVALID_QUESTIONNAIRE";
        } else if (errMsg.includes("duplicate")) {
          errorMessage = "A duplicate case is already in progress";
          errorCode = "DUPLICATE_CASE";
        } else if (errMsg.includes("pa tool")) {
          errorMessage = "This client has not been migrated to PA Tool";
          errorCode = "PA_TOOL_NOT_MIGRATED";
        } else if (errMsg.includes("epa configuration") || errMsg.includes("configuration")) {
          errorMessage = "An ePA configuration is not present for this tenant";
          errorCode = "EPA_CONFIG_MISSING";
        } else if (errMsg.includes("icd") || errMsg.includes("diagnosis")) {
          errorMessage = "One or more diagnosis codes are invalid";
          errorCode = "INVALID_ICD10";
        }

        // fallback error details
        if (responseData?.error_code && !errorCode) {
          errorCode = responseData.error_code;
        }
        if (responseData?.details) {
          details = responseData.details;
        }
      }

      setCreateError({ message: errorMessage, errorCode, details });
      showAlert({ type: "error", style: "banner", message: errorMessage });

      // Log specific error analytics
      const eventName = errorCode ? ERROR_CODE_TO_EVENT[errorCode] : null;
      logEvent(ANALYTIC_EVENTS.ERRORS[eventName] || ANALYTIC_EVENTS.ERRORS.caseCreate);

      // Scroll to the error
      window.scrollTo({ top: 0, behavior: "smooth" });
    }

  }, [showAlert, form]);

  const resetQuestionnaires = () => {
    form.setValue("questionnaires", null);
    form.setValue("questionnaire_responses", null);
    questionnaireForm.reset();
  };

  const goToCase = useCallback((caseId: string) => {
    const formValues = form.getValues();
    const params = {
      case_id: caseId,
      member_date_of_birth: formValues.member_date_of_birth,
      prescriber_npi: formValues.npi,
      external_member_id: formValues.external_member_id,
    };
    const encodedParams = new URLSearchParams();
    for (const key in params) {
      if (params[key]) {
        encodedParams.append(key, params[key]);
      }
    }
    navigate(`${getThemeURL("PA_CASE")}?${encodedParams.toString()}`);
  }, [navigate]);

  return (
    <div className={styles.PACreateFormContainer}>
      <Stack spacing="large">
        <Shelf className={styles.PACreateFormSectionTitle}>
          <PageTitle>New Prior Authorization Case</PageTitle>
          <Shelf.Spacer />
        </Shelf>

        {/* Error Display */}
        {createError && <PACreateErrorDisplay error={createError} />}

        {loading && (
          <Stack className={styles.LoadingContainer} alignment="center">
            <Heading className={styles.LoadingText}>Loading patient data...</Heading>
            <Spinner className={styles.LoadingSpinner} size="large" />
          </Stack>
        )}

        {!loading
          && <CaseTestClaimForm
            form={form}
            goToNextStep={goToNextStep}
            resetQuestionnaires={resetQuestionnaires}
          />
        }

        {!loading && formProgress >= FormStates.Questionnaire
          && <CaseQuestionnaireForm
            form={form}
            questionnaireForm={questionnaireForm}
            goToNextStep={createPriorAuthCase}
            prefillQuestions={prefillQuestions}
            showAlternateFaxNumberField={showPrescriberFaxField}
          />
        }
      </Stack>
    </div>
  );
};

export default PACreatePage;
